import React from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import EmployeeSearch from '../../../common/EmployeeSearch';
import { EMPLOYEE_RETURN_TYPES } from '../../../../constants/selectorReturnTypes';
import {
  ACTION_ITEM_STATUSES,
  ACTION_ITEM_PRIORITIES,
  ACTION_ITEM_PRIORITY_OPTIONS,
} from './ActionItemCard';
import RichTextEditor from '../../../common/RichTextEditor';
import { toPickerValue } from '../../../../utils/dateOnly';

// Size configurations for full and compact variants
const SIZE_CONFIG = {
  full: {
    padding: 2,
    formSpacing: 2,
    formControlMinWidth: 120,
    datePickerMinWidth: 150,
    inputHeight: '40px',
    fontSize: undefined,
    buttonFontSize: undefined,
    buttonPadding: undefined,
    headerIconSize: 20,
    backgroundColor: 'white',
    headerBackground: '#f5f7fa',
    borderColor: '#e0e0e0',
  },
  compact: {
    padding: 1.5,
    formSpacing: 1.5,
    formControlMinWidth: 100,
    datePickerMinWidth: 130,
    inputHeight: '36px',
    fontSize: '13px',
    buttonFontSize: '12px',
    buttonPadding: 0.5,
    headerIconSize: 16,
    backgroundColor: '#FAFBFC',
    headerBackground: '#F3F4F6',
    borderColor: '#E5E7EB',
  },
};

/**
 * ActionItemForm - Reusable form component for adding/editing action items
 *
 * @param {Object} props
 * @param {'full' | 'compact'} props.variant - Size variant (default: 'full')
 * @param {Object} props.item - The action item data being edited/added
 * @param {Function} props.onChange - Callback when any field changes: (updatedItem) => void
 * @param {Function} props.onSave - Callback when Save/Add button is clicked
 * @param {Function} props.onCancel - Callback when Cancel button is clicked
 * @param {boolean} props.isLoading - Whether the form is in a loading state
 * @param {boolean} props.isNew - Whether this is a new item (shows "Add" button) or editing (shows "Save" button)
 * @param {boolean} props.showHeader - Whether to show the form header (default: true for new items)
 * @param {string} props.headerTitle - Custom header title (default: "New Action Item")
 */
const ActionItemForm = ({
  variant = 'full',
  item = {},
  onChange,
  onSave,
  onCancel,
  isLoading = false,
  isNew = true,
  showHeader = true,
  headerTitle = 'New Action Item',
}) => {
  const config = SIZE_CONFIG[variant];

  const handleFieldChange = (field, value) => {
    onChange?.({ ...item, [field]: value });
  };

  const handleEmployeeChange = (employee) => {
    onChange?.({
      ...item,
      ownerEmployeeId: employee?.id || null,
      ownerEmployee: employee,
    });
  };

  const handleDeadlineChange = (newValue) => {
    if (newValue && newValue.isValid()) {
      handleFieldChange('deadlineAt', newValue.format('YYYY-MM-DD'));
    } else {
      handleFieldChange('deadlineAt', null);
    }
  };

  const formContent = (
    <Box sx={{ p: config.padding }}>
      <TextField
        fullWidth
        label="Title"
        placeholder="Enter action item title..."
        value={item.title || ''}
        onChange={(e) => handleFieldChange('title', e.target.value)}
        disabled={isLoading}
        size="small"
        sx={{ mb: config.formSpacing }}
        InputProps={config.fontSize ? { sx: { fontSize: config.fontSize } } : undefined}
        InputLabelProps={config.fontSize ? { sx: { fontSize: config.fontSize } } : undefined}
      />
      <Box sx={{ mb: config.formSpacing }}>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
          {isNew ? 'Description (optional)' : 'Description'}
        </Typography>
        <RichTextEditor
          variant="compact"
          value={item.description || ''}
          onChange={(value) => handleFieldChange('description', value)}
          minHeight={220}
          disabled={isLoading}
        />
      </Box>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
        <Box sx={{ display: 'flex', gap: config.formSpacing, mb: config.formSpacing, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <EmployeeSearch
            value={item.ownerEmployee || null}
            onChange={handleEmployeeChange}
            label="Owner"
            placeholder="Select owner..."
            width={200}
            height={config.inputHeight}
            returnValue={EMPLOYEE_RETURN_TYPES.OBJECT}
            disabled={isLoading}
            sx={{
              flex: '1 1 200px',
              minWidth: 200,
              ...(config.fontSize && { '& .MuiInputBase-input': { fontSize: config.fontSize } }),
            }}
          />
          <FormControl size="small" sx={{ minWidth: config.formControlMinWidth }}>
            <InputLabel sx={config.fontSize ? { fontSize: config.fontSize } : undefined}>Priority</InputLabel>
            <Select
              value={item.priority || ACTION_ITEM_PRIORITIES.MEDIUM}
              onChange={(e) => handleFieldChange('priority', e.target.value)}
              label="Priority"
              disabled={isLoading}
              sx={config.fontSize ? { fontSize: config.fontSize } : undefined}
            >
              {ACTION_ITEM_PRIORITY_OPTIONS.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  sx={config.fontSize ? { fontSize: config.fontSize } : undefined}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: config.formControlMinWidth }}>
            <InputLabel sx={config.fontSize ? { fontSize: config.fontSize } : undefined}>Status</InputLabel>
            <Select
              value={item.status || ACTION_ITEM_STATUSES.OPEN}
              onChange={(e) => handleFieldChange('status', e.target.value)}
              label="Status"
              disabled={isLoading}
              sx={config.fontSize ? { fontSize: config.fontSize } : undefined}
            >
              {Object.values(ACTION_ITEM_STATUSES).map((status) => (
                <MenuItem
                  key={status}
                  value={status}
                  sx={config.fontSize ? { fontSize: config.fontSize } : undefined}
                >
                  {status === 'InProgress' ? 'In Progress' : status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <DatePicker
            label="Deadline"
            value={toPickerValue(item.deadlineAt)}
            onChange={handleDeadlineChange}
            disabled={isLoading}
            slotProps={{
              textField: {
                size: 'small',
                sx: {
                  minWidth: config.datePickerMinWidth,
                  ...(config.fontSize && { '& .MuiInputBase-input': { fontSize: config.fontSize } }),
                },
                inputProps: { placeholder: 'DD/MM/YYYY' }
              }
            }}
          />
        </Box>
      </LocalizationProvider>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="contained"
          size="small"
          onClick={onSave}
          disabled={!item.title?.trim() || !item.ownerEmployeeId || isLoading}
          sx={config.buttonFontSize ? { fontSize: config.buttonFontSize, py: config.buttonPadding } : undefined}
        >
          {isNew ? 'Add' : 'Save'}
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={onCancel}
          disabled={isLoading}
          sx={config.buttonFontSize ? { fontSize: config.buttonFontSize, py: config.buttonPadding } : undefined}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );

  // For editing (not new), just return the form content without wrapper
  if (!isNew && !showHeader) {
    return formContent;
  }

  // For new items or when header is requested, wrap in Paper with header
  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${config.borderColor}`,
        borderRadius: '4px',
        mb: config.formSpacing,
        overflow: 'hidden',
        backgroundColor: config.backgroundColor,
      }}
    >
      {showHeader && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: config.padding,
            backgroundColor: config.headerBackground,
            borderBottom: `1px solid ${config.borderColor}`,
          }}
        >
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon sx={{ color: '#6B7280', fontSize: config.headerIconSize }} />
            <Typography
              variant="body2"
              fontWeight={500}
              color="text.secondary"
              sx={config.fontSize ? { fontSize: config.fontSize } : undefined}
            >
              {headerTitle}
            </Typography>
          </Box>
          <KeyboardArrowUpIcon sx={config.fontSize ? { fontSize: config.headerIconSize } : undefined} />
        </Box>
      )}
      {formContent}
    </Paper>
  );
};

export default ActionItemForm;
